import React from 'react';
import Tooltip from '../general/Tooltip';
import { useNova } from './NovaContext';
import { novaThemes, novaYellow } from './novaTheme';
import { NovaSunIcon, NovaMoonIcon } from './novaIcons';

interface NovaThemeToggleProps {
  segmentWidth?: number;
  segmentHeight?: number;
  iconSize?: number;
  className?: string;
}

// Segmented light/dark switch shared by the panel header and the standalone
// page's sidebar — sizing varies by context, so callers pass explicit
// dimensions rather than this owning a fixed size.
const NovaThemeToggle: React.FC<NovaThemeToggleProps> = ({
  segmentWidth = 30,
  segmentHeight = 26,
  iconSize = 15,
  className,
}) => {
  const { theme, toggleTheme } = useNova();
  const surfaces = novaThemes[theme];

  const segmentStyle = (active: boolean): React.CSSProperties => ({
    width: segmentWidth,
    height: segmentHeight,
    background: active ? surfaces.surfaces.themeToggleActive : 'transparent',
    boxShadow: active && theme === 'light' ? '0 1px 2px rgba(0,0,0,0.1)' : undefined,
    color: active
      ? theme === 'dark'
        ? novaYellow.high
        : surfaces.ink.heading
      : surfaces.ink.chromeIcon,
  });

  return (
    <div
      className={`flex items-center rounded-full p-[2px] ${className ?? ''}`}
      style={{ gap: 2, background: surfaces.surfaces.themeToggleTrack, border: `1px solid ${surfaces.surfaces.themeToggleTrackBorder}` }}
    >
      <Tooltip placement="bottom" content="Light mode">
        <button
          type="button"
          aria-label="Light mode"
          onClick={() => theme !== 'light' && toggleTheme()}
          className="flex cursor-pointer items-center justify-center rounded-full transition-colors"
          style={segmentStyle(theme === 'light')}
        >
          <span style={{ width: iconSize, height: iconSize }} className="flex items-center justify-center">
            <NovaSunIcon className="h-full w-full" />
          </span>
        </button>
      </Tooltip>
      <Tooltip placement="bottom" content="Dark mode">
        <button
          type="button"
          aria-label="Dark mode"
          onClick={() => theme !== 'dark' && toggleTheme()}
          className="flex cursor-pointer items-center justify-center rounded-full transition-colors"
          style={segmentStyle(theme === 'dark')}
        >
          <span style={{ width: iconSize, height: iconSize }} className="flex items-center justify-center">
            <NovaMoonIcon className="h-full w-full" />
          </span>
        </button>
      </Tooltip>
    </div>
  );
};

export default NovaThemeToggle;
